import DeleteOutline from '@mui/icons-material/DeleteOutline'
import { Box, IconButton, Tooltip } from '@mui/material'
import { useState } from 'react'
import { COMPLAINT_SEVERITY_OPTIONS } from 'src/consumer/constants'
import {
  commonStylesPrescriptionSelect,
  loadOptionsComplaints,
} from '../../../PrescriptionMakingPageComponents'
import {
  ComplaintCreatableSelect,
  DiagnosisDurationSelect,
} from '../../../PrescriptionMakingPageStyles'
import SegmentedPillSelector from '../../common/SegmentedPillSelector'
import {
  INITIAL_DIAGNOSIS_OPTION_ARRAY_LIST,
  loadOptionsDiagnosisDuration,
} from '../../diagnosis/util'

function normalize(value) {
  return value == null ? '' : String(value).trim().toLowerCase()
}

/**
 * Lean complaints entry (eka.care-style Symptoms card): a persistent search box that keeps
 * appending structured rows. Each selected complaint renders as one compact line — name +
 * an optional "For Duration" dropdown + optional Severity pills + delete. No table header,
 * no empty placeholder rows, no add button — you just keep typing/selecting.
 */
export default function ComplaintsTabularInput({
  complaintsList,
  onChangeComplaints,
  clinicId,
  clinicBrandId,
  recommendedComplaintIds,
  prescriptionViewSettings,
  menuPortalTarget,
}) {
  const list = Array.isArray(complaintsList) ? complaintsList : []
  // Remount the search select after each add so it resets cleanly, ready for the next entry.
  const [searchKey, setSearchKey] = useState(0)

  const portal = menuPortalTarget || (typeof document !== 'undefined' ? document.body : null)
  const selectMenuStyles = {
    ...commonStylesPrescriptionSelect,
    menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
    menu: (provided, state) => ({
      ...provided,
      ...commonStylesPrescriptionSelect.menu(provided, state),
      zIndex: 9999,
      position: 'absolute',
    }),
  }

  function updateRow(idx, patch) {
    onChangeComplaints(list.map((complaint, index) => (index === idx ? { ...complaint, ...patch } : complaint)))
  }

  function deleteRow(idx) {
    onChangeComplaints(list.filter((_, index) => index !== idx))
  }

  function addComplaint(option) {
    if (!option) {
      return
    }
    const key = normalize(option?.value ?? option?.label)
    const alreadyPresent =
      key && list.some((complaint) => normalize(complaint?.value ?? complaint?.label) === key)
    if (!alreadyPresent) {
      onChangeComplaints([...list, option]) // parent capitalises the label
    }
    setSearchKey((k) => k + 1)
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
      {list.length > 0 && (
        <Box
          sx={{
            border: '1px solid rgba(67, 56, 202, 0.12)',
            borderRadius: '10px',
            overflow: 'hidden',
            background: '#fff',
          }}
        >
          {list.map((complaint, idx) => (
            <ComplaintRow
              key={idx}
              complaint={complaint}
              isLast={idx === list.length - 1}
              portal={portal}
              menuPortalTarget={menuPortalTarget}
              selectMenuStyles={selectMenuStyles}
              onDuration={(val) => updateRow(idx, { duration: val })}
              onSeverity={(val) => updateRow(idx, { severity: val })}
              onDelete={() => deleteRow(idx)}
            />
          ))}
        </Box>
      )}

      <ComplaintCreatableSelect
        key={searchKey}
        id="complaintsAddSelection"
        classNamePrefix="complaints_add_selection"
        placeholder="Start typing Complaint…"
        createOptionPosition="first"
        openMenuOnFocus
        isSearchable
        isClearable={false}
        defaultOptions
        loadOptions={loadOptionsComplaints}
        getOptionValue={(option) => option?.value}
        getOptionLabel={(option) => option?.label}
        value={null}
        controlShouldRenderValue={false}
        onChange={(val) => addComplaint(val)}
        onCreateOption={(input) =>
          addComplaint({ apiParam: 'health-complaint', value: input, label: input, __isNew__: true })
        }
        debounceTimeout={200}
        additional={{
          page: 1,
          clinicId,
          clinicBrandId,
          recommendedHealthComplaintIds: recommendedComplaintIds,
          prescriptionViewSettings,
        }}
        styles={selectMenuStyles}
        menuPortalTarget={portal}
        menuPosition={menuPortalTarget ? 'fixed' : undefined}
        components={{ ClearIndicator: () => null }}
      />
    </Box>
  )
}

function ComplaintRow({
  complaint,
  isLast,
  portal,
  menuPortalTarget,
  selectMenuStyles,
  onDuration,
  onSeverity,
  onDelete,
}) {
  const name = complaint?.label || complaint?.title || complaint?.value || ''
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
        px: 1.25,
        py: 1,
        borderBottom: isLast ? 'none' : '1px solid rgba(67, 56, 202, 0.08)',
        transition: 'background 0.15s ease',
        '&:hover': { background: 'rgba(67, 56, 202, 0.02)' },
      }}
    >
      <Box
        sx={{
          flex: '1 1 140px',
          minWidth: 0,
          fontSize: 14,
          fontWeight: 500,
          color: '#1e293b',
          wordBreak: 'break-word',
        }}
      >
        {name}
      </Box>

      {/* Controls grouped so they wrap together below the name on narrow screens. minWidth:0 (not
          flexShrink:0) lets the group shrink to the row width when it wraps, so its own flexWrap
          wraps the duration + severity pills onto separate lines instead of overflowing the card. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
        <Box sx={{ flex: '0 0 auto', minWidth: 128, maxWidth: 168 }}>
          <DiagnosisDurationSelect
            id="complaint_for_duration"
            classNamePrefix="complaint_for_duration_selection"
            placeholder="For Duration"
            isSearchable
            isClearable
            defaultOptions={false}
            loadOptions={loadOptionsDiagnosisDuration}
            getOptionValue={(option) => option?.id}
            getOptionLabel={(option) => option?.title}
            isOptionDisabled={(option) =>
              INITIAL_DIAGNOSIS_OPTION_ARRAY_LIST?.map((o) => o?.id).includes(option?.id)
            }
            value={complaint?.duration ? { id: complaint?.duration, title: complaint?.duration } : null}
            onChange={(val) => onDuration(val?.id || '')}
            additional={{}}
            styles={selectMenuStyles}
            menuPortalTarget={portal}
            menuPosition={menuPortalTarget ? 'fixed' : undefined}
            components={{ ClearIndicator: () => null }}
          />
        </Box>

        <SegmentedPillSelector
          options={COMPLAINT_SEVERITY_OPTIONS}
          value={complaint?.severity}
          onChange={(val) => onSeverity(val)}
          ariaLabel="Complaint severity"
        />

        <Tooltip title="Remove complaint" arrow>
          <IconButton onClick={onDelete} size="small" sx={{ color: '#ef4444', flexShrink: 0 }}>
            <DeleteOutline sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )
}
